import * as React from 'react'
import { Formik, Field, FieldProps, FormikProps } from 'formik'
import styled from 'styled-components'

import * as yup from '@owl-nest/validators'
import * as plume from '@ulule/owl-kit-components/next'
import { type RequestFailure, request } from '@owl-nest/api-client'

import { InsertMediaData, InsertMediaPrecursor } from './insertMediaData'

export type InsertMediaModalProps = {
  className?: string
  open: boolean
  onConfirm: (data: InsertMediaData) => void
  onDismiss: () => void
  initialData?: Partial<InsertMediaPrecursor>
  translations: {
    title: string
    fields: {
      url: { label: string; placeholder: string }
      ratio: { label: string; placeholder: string }
    }

    errors: {
      badRequest: string
      internal: string
    }
    submitButton: string
  }
  features?: {
    embed?: EmbedFeature
  }
}

export function InsertMediaModal({
  className,
  open,
  onConfirm,
  onDismiss,
  initialData = {},
  translations,
  features,
}: InsertMediaModalProps): React.ReactElement<InsertMediaModalProps> {
  return (
    <Modal className={className} closable onClose={onDismiss} open={open}>
      {/* we don't use <form> because this Modal might live within a form and
      html5 forbids nested <form> elements */}
      <plume.styles.heading.XS>{translations.title}</plume.styles.heading.XS>
      <Formik
        initialValues={{ url: '', ratio: '', ...initialData }}
        onSubmit={async (values, helpers) => {
          try {
            const data = await (features?.embed?.get ?? defaultGetEmbed)(values.url, values.ratio)
            onConfirm(data)
          } catch (error) {
            if (isRequestFailure(error) && [400, 404].includes(error.status)) {
              helpers.setFieldError('url', translations.errors.badRequest)
            } else {
              helpers.setFieldError('url', translations.errors.internal)
            }
          }
        }}
        validationSchema={schema}
        validateOnBlur={false}
      >
        {(formikProps: FormikProps<InsertMediaPrecursor>) => {
          return (
            <>
              <Field name="url">
                {({ field, meta }: FieldProps<InsertMediaPrecursor['url']>) => {
                  return (
                    <plume.TextField
                      label={translations.fields.url.label}
                      placeholder={translations.fields.url.placeholder}
                      {...field}
                      error={meta.touched && meta.error}
                    />
                  )
                }}
              </Field>
              <Field name="ratio">
                {({ field, meta }: FieldProps<InsertMediaPrecursor['url']>) => {
                  return (
                    <RatioInput
                      type="number"
                      min="0"
                      max="100"
                      label={translations.fields.ratio.label}
                      placeholder={translations.fields.ratio.placeholder}
                      {...field}
                      error={meta.touched && meta.error}
                    />
                  )
                }}
              </Field>

              <plume.Button
                // type="button" is essential, because we don't want to accidentally
                // submit a parent <form />
                type="button"
                loading={formikProps.isSubmitting}
                onClick={formikProps.submitForm}
              >
                {translations.submitButton}
              </plume.Button>
            </>
          )
        }}
      </Formik>
    </Modal>
  )
}

async function defaultGetEmbed(url: string, ratio: string): Promise<InsertMediaData> {
  const response = await request<{ html: string }>('/editor/embed/', {
    method: 'post',
    type: 'json',
    body: { url },
  })

  return (
    await response.nextAsync(async (response) => {
      return {
        html: canResizeIframe(url)
          ? await setIframeSize(response.body.html, ratio === '' ? undefined : Number(ratio) / 100)
          : response.body.html,
        url,
        ratio,
      }
    })
  ).doThrow()
}

const EXCLUDED_SERVICES = ['circuitlab']

function canResizeIframe(href: string): boolean {
  try {
    const url = new URL(href)
    for (const service of EXCLUDED_SERVICES) {
      if (url.hostname.includes(service)) {
        return false
      }
    }
    return true
  } catch (error) {
    return false
  }
}

async function setIframeSize(html: string, ratio?: number): Promise<string> {
  const container = document.createElement('div')
  container.style.position = 'absolute'
  container.style.top = '0'
  container.style.left = '0'
  container.style.zIndex = '-1'
  container.style.visibility = 'hidden'
  container.style.pointerEvents = 'none'

  const sizer = document.createElement('div')
  const ratioer = document.createElement('div')
  container.appendChild(sizer)
  sizer.appendChild(ratioer)
  document.body.appendChild(container)

  ratioer.innerHTML = html

  const promises: Promise<void>[] = []
  sizer.querySelectorAll('img, iframe').forEach((element) => {
    promises.push(
      new Promise((res) => {
        element.addEventListener('load', () => {
          res()
        })
      }),
    )
  })
  await Promise.all(promises)

  const { width, height } = container.getBoundingClientRect()

  const resizeRatio = ratio === undefined ? (width > height ? 0.9 : 0.5) : ratio
  const embedRatio = height / width

  ratioer.style.paddingBottom = `${embedRatio * 100}%`
  ratioer.style.position = 'relative'
  ratioer.classList.add('embed-ratio')

  sizer.style.width = `${resizeRatio * 100}%`
  sizer.classList.add('embed-sizer')

  return container.innerHTML
}

const schema = yup.object().shape({
  url: yup.url().required(),
  ratio: yup.number().min(0).max(100),
})

function isRequestFailure(error: unknown): error is RequestFailure {
  return typeof error === 'object' && error !== null && 'status' in error
}

type EmbedFeature = {
  get?: (url: string, ratio: string, maxWidth?: number) => Promise<InsertMediaData>
}

const RatioInput = styled(plume.TextField)`
  ${plume.styles.input.InputsWrapper} {
    width: 100px;
  }
`

const Modal = styled(plume.Modal)`
  ${plume.styles.modal.Body} {
    ${plume.styles.heading.XS} {
      margin: 0 0 20px;
    }

    ${plume.styles.field.Label} {
      margin: 20px 0 40px;
    }

    ${plume.Button} {
      width: 100%;
    }
  }
`
